import { isLeft, isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import type { HubspotTicketAutomatedReviewData } from '../types';
import { normalizeAutomatedReview } from '../utils';

interface UseHubspotTicketAutomatedReviewParams {
  runId: string | null;
  trpc: TRPC | null;
}

interface UseHubspotTicketAutomatedReviewResult {
  data: HubspotTicketAutomatedReviewData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useHubspotTicketAutomatedReview({
  runId,
  trpc,
}: UseHubspotTicketAutomatedReviewParams): UseHubspotTicketAutomatedReviewResult {
  const [data, setData] = useState<HubspotTicketAutomatedReviewData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const fetchAutomatedReview = useCallback(
    async ({ resetData = false }: { resetData?: boolean } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      const isCurrentRequest = () => requestIdRef.current === requestId;

      if (resetData) {
        setData(null);
      }

      if (!runId) {
        if (isCurrentRequest()) {
          setError(null);
          setIsLoading(false);
        }
        return;
      }

      if (!trpc || !trpc.client) {
        if (isCurrentRequest()) {
          setError(new Error('TRPC context not initialized'));
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await trpc.client.getHubspotTicketAutomatedReview.query({
          runId,
        });

        if (!isCurrentRequest()) {
          return;
        }

        if (isRight(result)) {
          setData(normalizeAutomatedReview(result.right));
        } else if (isLeft(result)) {
          setError(
            new Error(
              result.left.message || 'Failed to fetch automated review',
            ),
          );
        }
      } catch (err) {
        if (!isCurrentRequest()) {
          return;
        }

        captureAndReportError('Error fetching automated review:', err);
        setError(err as Error);
      } finally {
        if (isCurrentRequest()) {
          setIsLoading(false);
        }
      }
    },
    [runId, trpc],
  );

  useEffect(() => {
    void fetchAutomatedReview({ resetData: true });

    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchAutomatedReview]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAutomatedReview,
  };
}
